import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';
import { calculateDateRange, DashboardPeriod } from '@/utils/dashboardDateUtils';

// ============= Interfaces =============

interface HeatmapCell {
  day: number;
  hour: number;
  bookings: number;
  avgCapacity: number;
  occupationRate: number;
}

interface GrowthDataPoint {
  month: string;
  members_count: number;
  revenue: number;
  previous_members_count: number;
  previous_revenue: number;
}

interface MemberEvolutionPoint {
  bucket: string;
  new_members: number;
  renewals: number;
}

interface InsightData {
  type: string;
  title: string;
  description: string;
  data: any;
}

interface CoachPerformance {
  coach_id: string;
  coach_name: string;
  classes_count: number;
  total_bookings: number;
  avg_occupation: number;
  total_revenue: number;
}

interface DashboardV2State {
  // KPIs
  totalMembers: number;
  classesToday: number;
  totalBookings: number;
  revenue: number;
  newMembers: number;
  renewals: number;
  averageOccupation: number;
  retentionRate: number;
  kpisLoading: boolean;
  kpisError: string | null;

  // Heatmap
  heatmapData: HeatmapCell[];
  heatmapLoading: boolean;
  heatmapError: string | null;

  // Growth Chart
  growthData: GrowthDataPoint[];
  growthLoading: boolean;
  growthError: string | null;

  // Member Evolution Chart
  memberEvolutionData: MemberEvolutionPoint[];
  memberEvolutionLoading: boolean;
  memberEvolutionError: string | null;

  // Insights
  insights: InsightData[];
  insightsLoading: boolean;
  insightsError: string | null;

  // Coach Performance
  coachPerformanceData: CoachPerformance[];
  coachPerformanceLoading: boolean;
  coachPerformanceError: string | null;

  // Filters
  filters: {
    period: DashboardPeriod;
  };

  // Actions
  fetchAllKPIs: (period: DashboardPeriod) => Promise<void>;
  fetchHeatmap: (period: DashboardPeriod) => Promise<void>;
  fetchGrowthData: () => Promise<void>;
  fetchMemberEvolution: (period: DashboardPeriod) => Promise<void>;
  fetchInsights: (period: DashboardPeriod) => Promise<void>;
  fetchCoachPerformance: (period: DashboardPeriod) => Promise<void>;
  setFilters: (filters: Partial<DashboardV2State['filters']>) => void;
  resetFilters: () => void;
}

// ============= Store =============

export const useDashboardV2Store = create<DashboardV2State>((set, get) => ({
  // Initial state
  totalMembers: 0,
  classesToday: 0,
  totalBookings: 0,
  revenue: 0,
  newMembers: 0,
  renewals: 0,
  averageOccupation: 0,
  retentionRate: 0,
  kpisLoading: false,
  kpisError: null,

  heatmapData: [],
  heatmapLoading: false,
  heatmapError: null,

  growthData: [],
  growthLoading: false,
  growthError: null,

  memberEvolutionData: [],
  memberEvolutionLoading: false,
  memberEvolutionError: null,

  insights: [],
  insightsLoading: false,
  insightsError: null,

  coachPerformanceData: [],
  coachPerformanceLoading: false,
  coachPerformanceError: null,

  filters: {
    period: 'month',
  },
  
  // ============= Actions =============

  fetchAllKPIs: async (period: DashboardPeriod) => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ 
        kpisError: 'No gym ID found',
        totalMembers: 0,
        classesToday: 0,
        totalBookings: 0,
        revenue: 0,
        kpisLoading: false
      });
      return;
    }

    set({ kpisLoading: true, kpisError: null });

    try {
      const { startDate, endDate } = calculateDateRange(period);

      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: { 
          action: 'get_all_kpis',
          gym_id: gymId,
          period,
          start_date: startDate,
          end_date: endDate
        }
      });

      if (error) throw error;

      set({ 
        totalMembers: data.totalMembers,
        classesToday: data.classesToday,
        totalBookings: data.totalBookings,
        revenue: data.revenue,
        newMembers: data.newMembers,
        renewals: data.renewals ?? 0,
        averageOccupation: data.averageOccupation,
        retentionRate: data.retentionRate,
        kpisLoading: false,
        kpisError: null
      });
    } catch (error: any) {
      console.error('Error fetching KPIs:', error);
      set({ kpisError: error.message || 'Failed to fetch KPIs', kpisLoading: false });
    }
  },

  fetchHeatmap: async (period: DashboardPeriod) => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ heatmapError: 'No gym ID found', heatmapData: [] });
      return;
    }

    set({ heatmapLoading: true, heatmapError: null });

    try {
      const { startDate, endDate } = calculateDateRange(period);

      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: { 
          action: 'get_heatmap',
          gym_id: gymId,
          period,
          start_date: startDate,
          end_date: endDate
        }
      });

      if (error) throw error;

      set({ heatmapData: data.data });
    } catch (error: any) {
      console.error('Error fetching heatmap:', error);
      set({ heatmapError: error.message || 'Failed to fetch heatmap' });
    } finally {
      set({ heatmapLoading: false });
    }
  },

  fetchMemberEvolution: async (period: DashboardPeriod) => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ memberEvolutionError: 'No gym ID found', memberEvolutionData: [] });
      return;
    }

    set({ memberEvolutionLoading: true, memberEvolutionError: null });

    try {
      const { startDate, endDate } = calculateDateRange(period);

      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: {
          action: 'get_member_evolution',
          gym_id: gymId,
          period,
          start_date: startDate,
          end_date: endDate
        }
      });

      if (error) throw error;

      set({ memberEvolutionData: data.points ?? [] });
    } catch (error: any) {
      console.error('Error fetching member evolution:', error);
      set({ memberEvolutionError: error.message || 'Failed to fetch member evolution' });
    } finally {
      set({ memberEvolutionLoading: false });
    }
  },

  fetchGrowthData: async () => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ growthError: 'No gym ID found', growthData: [] });
      return;
    }

    set({ growthLoading: true, growthError: null });

    try {
      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: { 
          action: 'get_growth_data',
          gym_id: gymId
        }
      });

      if (error) throw error;

      set({ growthData: data.data });
    } catch (error: any) {
      console.error('Error fetching growth data:', error);
      set({ growthError: error.message || 'Failed to fetch growth data' });
    } finally {
      set({ growthLoading: false });
    }
  },

  fetchInsights: async (period: DashboardPeriod) => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ insightsError: 'No gym ID found', insights: [] });
      return;
    }

    set({ insightsLoading: true, insightsError: null });

    try {
      const { startDate, endDate } = calculateDateRange(period);

      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: { 
          action: 'get_insights',
          gym_id: gymId,
          period,
          start_date: startDate,
          end_date: endDate
        }
      });

      if (error) throw error;

      set({ insights: data.data });
    } catch (error: any) {
      console.error('Error fetching insights:', error);
      set({ insightsError: error.message || 'Failed to fetch insights' });
    } finally {
      set({ insightsLoading: false });
    }
  },

  fetchCoachPerformance: async (period: DashboardPeriod) => {
    const gymId = getUserGymId();
    if (!gymId) {
      set({ coachPerformanceError: 'No gym ID found', coachPerformanceData: [] });
      return;
    }

    set({ coachPerformanceLoading: true, coachPerformanceError: null });

    try {
      const { startDate, endDate } = calculateDateRange(period);

      const { data, error } = await supabase.functions.invoke('dashboard-kpis', {
        body: { 
          action: 'get_coach_performance',
          gym_id: gymId,
          period,
          start_date: startDate,
          end_date: endDate
        }
      });

      if (error) throw error;

      set({ coachPerformanceData: data.data });
    } catch (error: any) {
      console.error('Error fetching coach performance:', error);
      set({ coachPerformanceError: error.message || 'Failed to fetch coach performance' });
    } finally {
      set({ coachPerformanceLoading: false });
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  resetFilters: () => {
    set({
      filters: {
        period: 'month',
      },
    });
  },
}));
